import BuildCircleOutlinedIcon from '@mui/icons-material/BuildCircleOutlined';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import React, { useEffect } from 'react';

import { themeColors } from '@/config/theme/themeColors.ts';
import {
  clearMaintenance,
  useMaintenanceState,
} from '@/contexts/maintenance/Maintenance.context.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { readTimeLabel } from '@/views/chats/utils/chatReadReceipts.util.ts';

const STATUS_URL = '/api/maintenance/status';
const STATUS_CHECK_INTERVAL_MS = 30_000;
const END_GRACE_MS = 60_000;

type MaintenanceStatusBody = {
  active?: boolean;
  canWork?: boolean;
};

const isOver = async (maintenanceEnd?: string): Promise<boolean> => {
  try {
    const response = await fetch(STATUS_URL, { cache: 'no-store', credentials: 'include' });
    if (response.ok) {
      const status = (await response.json()) as MaintenanceStatusBody;
      return !status.active || Boolean(status.canWork);
    }
  } catch {}
  return maintenanceEnd !== undefined && dayjs().diff(maintenanceEnd) > END_GRACE_MS;
};

export const MaintenanceView: React.FC = () => {
  const { t } = useTranslationWithPrefix('views.util.maintenance');
  const { maintenanceEnd, maintenanceEndDate, maintenanceMessage, messagePl } =
    useMaintenanceState();

  useEffect(() => {
    const id = setInterval(async () => {
      if (await isOver(maintenanceEnd)) {
        clearMaintenance();
        globalThis.location.reload();
      }
    }, STATUS_CHECK_INTERVAL_MS);
    return () => clearInterval(id);
  }, [maintenanceEnd]);

  const endLabel = maintenanceEnd ? readTimeLabel(maintenanceEnd) : maintenanceEndDate;

  return (
    <Stack
      data-maintenance-screen
      spacing={2.5}
      sx={{
        alignItems: 'center',
        background: themeColors.white,
        height: '100vh',
        justifyContent: 'center',
        px: 4,
      }}
    >
      <BuildCircleOutlinedIcon sx={{ color: themeColors.accentColor, fontSize: 64 }} />
      <Typography
        sx={{ color: themeColors.accentColorDark, textAlign: 'center' }}
        variant="f32Medium"
      >
        {t('title')}
      </Typography>
      <Typography
        sx={{ color: themeColors.greyDarker, maxWidth: 640, textAlign: 'center' }}
        variant="f18Medium"
      >
        {maintenanceMessage ?? messagePl ?? t('fallbackMessage')}
      </Typography>
      {endLabel && (
        <Typography
          sx={{ color: themeColors.accentColorDark, textAlign: 'center' }}
          variant="f24Medium"
        >
          {t('backAt', { date: endLabel })}
        </Typography>
      )}
      <Typography sx={{ color: themeColors.greyDark, textAlign: 'center' }} variant="f14Regular">
        {t('autoReload')}
      </Typography>
    </Stack>
  );
};
